import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import {
  Button, PageShell, Skeleton, DataTable, StatusBadge, RowActions, Pagination, Input, SearchGrid, SearchGridItem,
  type DataTableColumn, type DataTableSort, toggleDataTableSort,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui';
import { Plus } from 'lucide-react';
import { useAllEventsQuery, useEventMutation } from '../hooks/useEventsQuery';
import {
  EMPTY_EVENT_FILTER,
  eventPeriodStatus,
  eventRegistrationNumbers,
  filterEvents,
  sortEvents,
  todayKst,
  type EventSort,
  type EventListFilter,
  type EventListRow,
  type EventPeriodStatus,
} from '../lib/eventListFilter';

const PAGE_SIZE = 20;

type EventRow = EventListRow & { eventKey: string };

const STATUS_BADGE: Record<EventPeriodStatus, { variant: 'info' | 'success' | 'warning'; label: string }> = {
  UNSET: { variant: 'info', label: '미정' },
  SCHEDULED: { variant: 'info', label: '예정' },
  ONGOING: { variant: 'success', label: '진행중' },
  ENDED: { variant: 'warning', label: '종료' },
};

/** 라디오 묶음(검색 조건) — 고도몰 검색 영역처럼 전체/값 라디오. */
const RadioRow = <V extends string>({
  name,
  value,
  options,
  onChange,
}: {
  name: string;
  value: V;
  options: ReadonlyArray<{ value: V; label: string }>;
  onChange: (v: V) => void;
}) => (
  <div className="flex flex-wrap items-center gap-4 text-[13px]" role="radiogroup" data-testid={`event-filter-${name}`}>
    {options.map((o) => (
      <label key={o.value} className="inline-flex items-center gap-1.5 whitespace-nowrap">
        <input type="radio" name={`event-filter-${name}`} checked={value === o.value} onChange={() => onChange(o.value)} />
        {o.label}
      </label>
    ))}
  </div>
);

/*
 * 이벤트 관리 목록. QA-356:
 * - 검색 조건: 이벤트명(부분일치) · 기간(시작~종료, 겹치는 이벤트) · 진행 상태 · 활성 여부 · 당첨자 발표.
 *   BE 목록이 검색을 받지 않아 전체를 받아 화면에서 거른다(useAllEventsQuery).
 * - 이벤트명 칸을 넓히고(종전 300px 고정) 기간 칸은 내용 폭으로. 이벤트명을 누르면 수정 화면(⋯ 메뉴의 수정 제거).
 * QA-365: 수기 "순서" 열을 없애고 기본 정렬을 진행상태 → 기간으로. 열 머리글을 눌러 오름/내림차순 정렬.
 */
export const EventListPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState<EventListFilter>(EMPTY_EVENT_FILTER);
  const [filter, setFilter] = useState<EventListFilter>(EMPTY_EVENT_FILTER);
  const query = useAllEventsQuery();
  const { remove } = useEventMutation();
  const [deleteTarget, setDeleteTarget] = useState<EventRow | null>(null);
  const [sort, setSort] = useState<EventSort | null>(null);

  const today = todayKst();
  const filtered = useMemo(
    () => sortEvents(filterEvents((query.data ?? []) as EventRow[], filter, today), sort, today),
    [query.data, filter, today, sort],
  );
  const registrationNumbers = useMemo(
    () => eventRegistrationNumbers((query.data ?? []) as EventRow[]),
    [query.data],
  );
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const patch = (p: Partial<EventListFilter>) => setDraft((prev) => ({ ...prev, ...p }));
  const apply = (next: EventListFilter) => {
    setFilter(next);
    setPage(1);
  };

  const columns: DataTableColumn<EventRow>[] = [
    {
      // QA-408 — 맨 왼쪽 No. QA-500 — 정렬을 바꿔도 이벤트마다 같은 등록 번호(등록 순 1부터)를 보여 준다.
      key: 'no',
      header: 'No',
      align: 'center',
      width: 64,
      cell: (row) => (
        <span className="tabular-nums text-xs" style={{ color: 'var(--content-muted)' }} data-testid="event-row-no">
          {registrationNumbers.get(row.eventId) ?? ''}
        </span>
      ),
    },
    {
      key: 'title',
      header: t('cms.events.cols.title'),
      headerAlign: 'center',
      sortable: true,
      cell: (row) => (
        <Link
          to={`/cms/events/${row.eventId}`}
          className="block min-w-[280px] truncate text-sm font-medium hover:underline"
          style={{ color: 'var(--content-strong)' }}
          title={row.title}
          data-testid="event-title-link"
        >
          {row.title}
        </Link>
      ),
    },
    {
      key: 'period',
      header: t('cms.events.cols.period'),
      align: 'center',
      width: 190,
      sortable: true,
      cell: (row) => (
        <span className="whitespace-nowrap text-xs tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.startDate} ~ {row.endDate}
        </span>
      ),
    },
    {
      key: 'periodStatus',
      header: t('cms.events.cols.periodStatus', { defaultValue: '진행상태' }),
      align: 'center',
      width: 104,
      sortable: true,
      cell: (row) => {
        const s = STATUS_BADGE[eventPeriodStatus(row.startDate, row.endDate, today)];
        return <span className="inline-block whitespace-nowrap"><StatusBadge variant={s.variant} label={s.label} /></span>;
      },
    },
    {
      key: 'isActive',
      header: t('cms.events.cols.isActive'),
      align: 'center',
      width: 88,
      sortable: true,
      cell: (row) => (
        <span className="inline-block whitespace-nowrap">
          <StatusBadge
            variant={row.isActive ? 'success' : 'info'}
            label={row.isActive ? t('cms.active') : t('cms.inactive')}
          />
        </span>
      ),
    },
    {
      key: 'isWinnersPublished',
      header: t('cms.events.cols.winnersAnnounced'),
      align: 'center',
      width: 112,
      sortable: true,
      cell: (row) => (
        <span className="inline-block whitespace-nowrap">
          <StatusBadge
            variant={row.isWinnersPublished ? 'success' : 'info'}
            label={row.isWinnersPublished ? t('cms.events.announced') : t('cms.events.notAnnounced')}
          />
        </span>
      ),
    },
    {
      // 핵심 운영 기능(응모 현황 / 당첨자 발표)은 행 버튼, 수정은 이벤트명 클릭, 삭제만 ⋯ 메뉴.
      key: 'actions',
      header: t('cms.events.cols.manage', { defaultValue: '관리' }),
      align: 'right',
      headerAlign: 'center',
      width: 248,
      cell: (row) => (
        <div className="flex items-center justify-end gap-1.5 whitespace-nowrap">
          <Button variant="outline" size="sm" onClick={() => navigate(`/cms/events/${row.eventId}/applications`)}>
            {t('cms.events.applications.viewLink', { defaultValue: '응모 현황' })}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => navigate(`/cms/events/${row.eventId}/winners`)}>
            당첨자 발표
          </Button>
          <RowActions
            triggerLabel={t('cms.events.rowActionsLabel', { defaultValue: '이벤트 액션' })}
            actions={[
              {
                key: 'delete',
                label: t('cms.delete'),
                variant: 'danger',
                onClick: () => setDeleteTarget(row),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <PageShell
      title={t('cms.events.title')}
      actions={
        <Link to="/cms/events/new">
          <Button size="sm">
            <Plus size={15} aria-hidden="true" />
            {t('cms.events.create')}
          </Button>
        </Link>
      }
      data-testid="event-list-page"
    >
      <form
        className="mb-4"
        onSubmit={(e) => {
          e.preventDefault();
          apply(draft);
        }}
      >
        <SearchGrid
          aria-label={t('cms.events.search.label', { defaultValue: '이벤트 검색' })}
          testId="event-search"
          footer={
            <>
              <Button type="submit" size="sm" data-testid="event-search-submit">
                {t('cms.events.search.submit', { defaultValue: '검색' })}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDraft(EMPTY_EVENT_FILTER);
                  apply(EMPTY_EVENT_FILTER);
                }}
              >
                {t('cms.events.search.reset', { defaultValue: '초기화' })}
              </Button>
            </>
          }
        >
          <SearchGridItem label={t('cms.events.search.keyword', { defaultValue: '이벤트명' })} htmlFor="event-keyword">
            <Input
              id="event-keyword"
              type="search"
              value={draft.keyword}
              onChange={(e) => patch({ keyword: e.target.value })}
              placeholder={t('cms.events.search.keywordPlaceholder', { defaultValue: '이벤트명 일부로 검색' })}
              className="w-full max-w-[360px]"
            />
          </SearchGridItem>
          <SearchGridItem label={t('cms.events.search.period', { defaultValue: '기간' })}>
            <div className="flex items-center gap-1.5">
              <Input type="date" value={draft.from} onChange={(e) => patch({ from: e.target.value })} aria-label={t('cms.events.search.from', { defaultValue: '시작일' })} className="w-full max-w-[170px]" />
              <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>~</span>
              <Input type="date" value={draft.to} onChange={(e) => patch({ to: e.target.value })} aria-label={t('cms.events.search.to', { defaultValue: '종료일' })} className="w-full max-w-[170px]" />
            </div>
          </SearchGridItem>
          <SearchGridItem label={t('cms.events.search.status', { defaultValue: '진행 상태' })}>
            <RadioRow
              name="status"
              value={draft.status}
              onChange={(status) => patch({ status })}
              options={[
                { value: 'ALL', label: '전체' },
                { value: 'SCHEDULED', label: '예정' },
                { value: 'ONGOING', label: '진행중' },
                { value: 'ENDED', label: '종료' },
              ]}
            />
          </SearchGridItem>
          <SearchGridItem label={t('cms.events.search.active', { defaultValue: '활성 여부' })}>
            <RadioRow
              name="active"
              value={draft.active}
              onChange={(active) => patch({ active })}
              options={[
                { value: 'ALL', label: '전체' },
                { value: 'ACTIVE', label: t('cms.active') },
                { value: 'INACTIVE', label: t('cms.inactive') },
              ]}
            />
          </SearchGridItem>
          <SearchGridItem label={t('cms.events.cols.winnersAnnounced')} span="full">
            <RadioRow
              name="winners"
              value={draft.winners}
              onChange={(winners) => patch({ winners })}
              options={[
                { value: 'ALL', label: '전체' },
                { value: 'ANNOUNCED', label: t('cms.events.announced') },
                { value: 'NOT_ANNOUNCED', label: t('cms.events.notAnnounced') },
              ]}
            />
          </SearchGridItem>
        </SearchGrid>
      </form>

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[44px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('cms.events.loadFailed')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('cms.retry')}
          </Button>
        </div>
      ) : (
        <>
          <p className="mb-2 text-[13px]" style={{ color: 'var(--content-muted)' }} data-testid="event-total">
            {t('cms.events.search.total', { defaultValue: '검색 {{n}}건', n: filtered.length.toLocaleString('ko-KR') })}
          </p>
          <DataTable
            columns={columns}
            rows={pageRows}
            rowKey={(row) => row.eventId}
            emptyState={t('cms.events.empty')}
            stickyHeader
            sort={sort as DataTableSort | null}
            onSortChange={(key) => {
              setSort((prev) => toggleDataTableSort(prev, key) as EventSort);
              setPage(1);
            }}
          />
        </>
      )}

      <Pagination
        total={filtered.length}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        className="mt-4"
      />

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('cms.events.title')} 삭제</DialogTitle>
            <DialogDescription>
              {t('cms.events.confirmDelete', { title: deleteTarget?.title ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('cms.cancel')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={remove.isPending}
              onClick={() => {
                if (deleteTarget) remove.mutate(deleteTarget.eventId, { onSuccess: () => setDeleteTarget(null) });
              }}
            >
              {t('cms.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
